/**
 * Utility functions for working with Tailwind CSS classes
 */

// Common Tailwind CSS utility class prefixes to filter out
const TAILWIND_PATTERNS = /^(px|py|pt|pb|pl|pr|mx|my|mt|mb|ml|mr|p-|m-|w-|h-|min-|max-|flex|grid|block|inline|hidden|absolute|relative|fixed|sticky|static|text-|font-|bg-|border|rounded|shadow|opacity-|z-|gap-|space-|divide-|ring-|outline-|transition|duration-|ease-|delay-|animate-|scale-|rotate-|translate-|skew-|origin-|cursor-|select-|resize-|scroll-|snap-|touch-|overflow-|overscroll-|truncate|whitespace-|break-|hyphens-|content-|items-|justify-|self-|place-|order-|col-|row-|auto-|float-|clear-|object-|aspect-|columns-|break-|box-|isolation-|mix-|backdrop-|filter-|blur-|brightness-|contrast-|grayscale-|hue-|invert-|saturate-|sepia-|drop-|fill-|stroke-|sr-|not-|group-|peer-|hover:|focus:|active:|disabled:|visited:|checked:|indeterminate:|default:|required:|valid:|invalid:|in-range:|out-of-range:|placeholder:|autofill:|read-only:|before:|after:|first:|last:|odd:|even:|first-of-type:|last-of-type:|only:|only-of-type:|target:|open:|empty:|focus-within:|focus-visible:|has:|is:|where:|dark:|light:|motion-|print:|ltr:|rtl:|sm:|md:|lg:|xl:|2xl:)/;

/**
 * Check if a class name is a Tailwind utility class
 */
export function isTailwindClass(className: string): boolean {
  if (!className) return false;
  return TAILWIND_PATTERNS.test(className);
}

/**
 * Extract the first meaningful (non-Tailwind) class from a className string
 * Returns null if no meaningful class is found
 */
export function extractMeaningfulClass(className: string): string | null {
  if (!className || typeof className !== 'string') return null;

  const classes = className.split(/\s+/);
  const meaningfulClass = classes.find(c => c && !isTailwindClass(c));

  return meaningfulClass || null;
}

/**
 * Extract all meaningful (non-Tailwind) classes from a className string
 */
export function extractMeaningfulClasses(className: string): string[] {
  if (!className || typeof className !== 'string') return [];

  const classes = className.split(/\s+/);
  return classes.filter(c => c && !isTailwindClass(c));
}

/**
 * Get a short, meaningful identifier for an HTML element
 * Useful for analytics, testing, and debugging
 *
 * Priority: id > data-testid > aria-label > meaningful class > tag:text snippet
 */
export function getElementIdentifier(element: HTMLElement, maxLength: number = 200): string {
  // Priority 1: Element ID
  if (element.id) {
    return element.id.substring(0, maxLength);
  }

  // Priority 2: data-testid attribute
  const testId = element.getAttribute('data-testid');
  if (testId) {
    return testId.substring(0, maxLength);
  }

  // Priority 3: aria-label attribute
  const ariaLabel = element.getAttribute('aria-label');
  if (ariaLabel) {
    return ariaLabel.substring(0, maxLength);
  }

  // Priority 4: First meaningful (non-Tailwind) class
  // Use getAttribute('class') to handle both HTML and SVG elements
  // (element.className returns SVGAnimatedString for SVG elements)
  const classAttr = element.getAttribute('class');
  const meaningfulClass = extractMeaningfulClass(classAttr || '');
  if (meaningfulClass) {
    return meaningfulClass.substring(0, maxLength);
  }

  // Priority 5: Fallback to tag name + text snippet
  const tagName = element.tagName.toLowerCase();
  const textSnippet = (element.textContent?.trim() || '').substring(0, 50);
  const fallback = textSnippet ? `${tagName}:${textSnippet}` : tagName;

  return fallback.substring(0, maxLength);
}
